// @ts-check

/**
 * @file Renders the account create/edit form.
 */

import {Form} from "@kaspernj/api-maker/build/form"
import Text from "@kaspernj/api-maker/build/utils/text"
import {useLocalSearchParams, useRouter} from "expo-router"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {createRef, memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native"
import {Account} from "../../frontend-models/account"
import Button from "../../components/button"
import buildRoutePath from "../../shared/build-route-path"
import Card from "../../components/card"
import errorMessage from "../../shared/error-message"
import FormTextInput from "../../components/form/form-text-input"
import {optionalRouteParam} from "../../shared/route-params"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"
import {useScreenLoading} from "../../components/board-layout/screen-loading"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} AccountsEditScreenProps */

/**
 * @typedef {object} AccountsEditScreenState
 * @property {Account | null} account Loaded account being edited.
 * @property {boolean} loading Whether account data is loading.
 * @property {boolean} saving Whether form submission is running.
 * @property {{kind: "error" | "success", text: string} | null} status Screen status message.
 */

/**
 * Account edit screen.
 * @augments {ShapeComponent<AccountsEditScreenProps, AccountsEditScreenState>}
 */
class AccountsEditScreen extends ShapeComponent {
  /** @type {string | null} */
  accountID
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  formObjectRef = createRef()
  /** @type {AccountsEditScreenState} */
  state = {
    account: /** @type {Account | null} */ null,
    loading: true,
    saving: false,
    status:
      /** @type {{kind: "error" | "success", text: string} | null} */ null
  }

  /** Initializes route params and account loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()
    const router = useRouter()

    const accountID = optionalRouteParam(params, "id", "Account ID")
    this.params = params
    this.router = router
    this.accountID = accountID
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      let cancelled = false
      const loadData = async () => {
        try {
          if (accountID) {
            const account = await Account.find(accountID)
            if (cancelled) return
            this.s.account = account
            this.s.loading = false
          } else {
            this.s.loading = false
          }
        } catch (error) {
          if (cancelled) return
          this.s.loading = false
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
        }
      }
      void loadData()
      return () => {
        cancelled = true
      }
    }, [accountID])
  }

  /**
   * Renders the account form.
   * @returns {ReactNode} Account form content.
   */
  render() {
    const {account, loading, saving, status} = this.s
    const {accountID} = this.tt
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="accountEditScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="accountEditScreen"
      >
        <Text
          style={styles[`screenTitle-${this.tt.themeName}`] ||= {
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 16
          }}
          testID="accountEditScreen/title"
        >
          {accountID ? _("Edit account") : _("New account")}
        </Text>
        <Form formObjectRef={this.formObjectRef} onSubmit={this.tt.onSubmit}>
          <Card
            style={styles.formCardSpacing ||= {
              gap: 12,
              maxWidth: 520,
              width: "100%"
            }}
            testID="accountEditScreen/form"
          >
            <FormTextInput
              defaultValue={account ? account.name() || "" : ""}
              label={_("Name")}
              name="name"
              testID="accountEditScreen/nameInput"
            />
            {status &&
              <Text
                style={styles[`status-${this.tt.themeName}-${status.kind}`] ||= {
                  color: status.kind === "success" ? this.tt.colors.successText : this.tt.colors.dangerText,
                  fontWeight: 600
                }}
                testID="accountEditScreen/statusMessage"
              >
                {status.text}
              </Text>
            }
            <Button
              disabled={saving}
              iconName="floppy-disk"
              label={saving ? _("Saving...") : _("Save")}
              onPress={this.tt.onSubmit}
              testID="accountEditScreen/saveButton"
            />
          </Card>
        </Form>
      </View>
    )
  }

  /**
   * Saves the account form.
   * @returns {Promise<void>} Resolves when save handling completes.
   */
  onSubmit = async () => {
    const {accountID, params, router} = this.tt
    const {account} = this.s
    const formObject = this.formObjectRef.current
    if (!formObject) return
    const values = formObject.asObject()
    this.s.saving = true
    this.s.status = null
    try {
      if (account && accountID) {
        await account.update({
          name: values.name || ""
        })
        router.push(buildRoutePath(params, `/accounts/${accountID}`))
      } else {
        const created = await Account.create({
          name: values.name || ""
        })
        router.push(buildRoutePath(params, `/accounts/${created.id()}`))
      }
    } catch (error) {
      this.s.saving = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }
}

/** @type {NamedExoticComponent<AccountsEditScreenProps>} */
const AccountsEditScreenComponent = memo(shapeComponent(AccountsEditScreen))

export default AccountsEditScreenComponent
